/**
 * WorldReferencePanel - the world price for a commodity, on its own terms.
 *
 * Answers one question: is this local price move a local story or a world
 * story? Maize in Kaduna rising while world maize is flat is a Nigerian
 * event. Both rising together is not.
 *
 * It is a separate panel with its own axis rather than a line on the local
 * price chart, and that is the whole design decision. When the commodity
 * pages show a local commodity, the Y axis is raw naira per bag, and a bag is
 * 25, 50, 75 or 100kg depending on the row. A world price in dollars per
 * tonne shares no axis with that. Drawing them together would produce
 * something that looks like a comparison and is not one. Converting the world
 * series into naira per bag to force them onto one axis would be worse: it
 * would invent a bag size for a benchmark that has no bags, and bury an
 * exchange rate assumption inside a line the reader believes is a price.
 *
 * So the two sit side by side, each labelled with its own unit, and the
 * reader compares shapes rather than heights. That is the honest comparison
 * and the one that actually answers the question.
 *
 * Renders nothing when the commodity has no world equivalent, which is true
 * of eighteen of the thirty eight we track. Millet, cowpea, sesame, chilli,
 * ginger, turmeric, hibiscus, dawadawa and barbara nut are not on any world
 * index, and an empty panel would imply we simply failed to load something.
 */

import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useWorldReference } from '../../services/queries';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "2026-08-01" to "Aug 2026". These are months, never days, so the day is
 *  deliberately never shown: printing "1 Aug" would invite reading a monthly
 *  average as a daily observation. */
function monthLabel(iso) {
  if (!iso) return '';
  const [y, m] = iso.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

function WorldReferencePanel({ commodityId, months = 60 }) {
  const { palette } = useThemeMode();
  const { data, isLoading } = useWorldReference(commodityId, months, {
    enabled: Boolean(commodityId),
  });

  const points = useMemo(
    () => (data?.points || []).map((p) => ({ ...p, label: monthLabel(p.period) })),
    [data],
  );

  // No mapping, or nothing loaded yet. Silence is the right answer for the
  // commodities that genuinely have no world price.
  if (isLoading || !data?.series || points.length === 0) return null;

  const latest = points[points.length - 1];
  const first = points[0];
  const change = first?.price ? ((latest.price - first.price) / first.price) * 100 : null;
  const rising = change != null && change > 0;
  const flat = change != null && change === 0;

  return (
    <Card sx={{ mt: 2.5 }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.25, borderBottom: `1px solid ${palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Icon icon="ph:globe-hemisphere-west" width={16} height={16} color={palette.textMuted} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: palette.text }}>
            World reference: {data.series.label}
          </Typography>
        </Box>
        {/* The unit and the cadence sit in the header, not a legend, because
            this is the thing a reader must not miss. */}
        <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary, mt: 0.4, lineHeight: 1.5 }}>
          Monthly average in {data.series.unit}, on its own scale. Not comparable
          to local prices by height; compare the shape of the two lines.
        </Typography>
      </Box>

      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: palette.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {monthLabel(latest.period)}
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: '1.15rem', color: palette.text }}>
              {latest.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              <Box component="span" sx={{ fontSize: '0.75rem', color: palette.textMuted, ml: 0.75 }}>
                {data.series.unit}
              </Box>
            </Typography>
          </Box>
          {change != null && (
            <Box>
              <Typography sx={{ fontSize: '0.7rem', color: palette.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {monthLabel(first.period)} to {monthLabel(latest.period)}
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: '1.15rem', color: flat ? palette.textSecondary : (rising ? palette.accent : palette.danger) }}>
                {rising ? '+' : ''}{change.toFixed(1)}%
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ width: '100%', height: 180 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: palette.textMuted }}
                tickLine={false}
                axisLine={{ stroke: palette.divider }}
                minTickGap={40}
              />
              <YAxis
                tick={{ fontSize: 10, fill: palette.textMuted }}
                tickLine={false}
                axisLine={false}
                width={52}
                domain={['auto', 'auto']}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: palette.navy,
                  border: 'none',
                  borderRadius: 10,
                  fontSize: '0.78rem',
                }}
                labelStyle={{ color: 'rgba(255,255,255,0.65)' }}
                itemStyle={{ color: '#fff', fontFamily: MONO }}
                formatter={(v) => [`${Number(v).toLocaleString()} ${data.series.unit}`, 'Monthly average']}
              />
              {/* Stepped, because each figure holds for a whole month. A
                  smooth line would imply daily movement we do not have. */}
              <Line
                type="stepAfter"
                dataKey="price"
                stroke={palette.textSecondary}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        {/* CC BY 4.0 obliges the credit wherever the data appears. */}
        <Typography sx={{ fontSize: '0.68rem', color: palette.textMuted, mt: 1.5, fontStyle: 'italic', lineHeight: 1.5 }}>
          {data.attribution} Published about a month in arrears, so the current
          month is not shown.
        </Typography>
      </CardContent>
    </Card>
  );
}

export default WorldReferencePanel;
